import { useRef, useState, useEffect, useLayoutEffect } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
} from "framer-motion";
import {
  Code2,
  Cpu,
  Database,
  Workflow,
  Zap,
  CheckCircle2,
} from "lucide-react";

/* Natural design dimensions of the 3D stage. Matches the SVG viewBox so the
   whole composition stays perfectly balanced — on smaller viewports we scale
   this fixed stage down as a single centered block instead of repositioning
   individual cards (which the 3D perspective would otherwise push off-center). */
const STAGE_W = 600;
const STAGE_H = 560;

/* A single floating layer: outer div controls 3D depth (translateZ),
   inner motion.div handles entrance + perpetual float loop. */
function FloatLayer({
  className,
  depth = 40,
  delay = 0,
  duration = 6,
  amplitude = 10,
  children,
}) {
  return (
    <div
      className={`absolute ${className}`}
      style={{ transform: `translateZ(${depth}px)`, transformStyle: "preserve-3d" }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1, y: [0, -amplitude, 0] }}
        transition={{
          opacity: { duration: 0.7, delay, ease: "easeOut" },
          scale: { duration: 0.7, delay, ease: "easeOut" },
          y: { duration, repeat: Infinity, ease: "easeInOut", delay },
        }}
        style={{ willChange: "transform" }}
      >
        {children}
      </motion.div>
    </div>
  );
}

export default function Hero3D() {
  const ref = useRef(null);
  const wrapRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);

  /* Scale the fixed-size stage to fit the available column width. On smaller
     screens we divide by a slightly larger effective width to leave room for
     the 3D perspective magnification of the outer cards (so nothing clips),
     while desktop keeps the original full-size composition. */
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => {
      const denom = window.innerWidth < 1024 ? STAGE_W * 1.4 : STAGE_W;
      setScale(Math.min(1, el.clientWidth / denom));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  const rotX = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), {
    stiffness: 110,
    damping: 18,
  });
  const rotY = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), {
    stiffness: 110,
    damping: 18,
  });

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <div
      ref={wrapRef}
      data-testid="hero-3d-wrap"
      className="relative w-full overflow-visible"
      style={{ height: STAGE_H * scale }}
    >
      <div className="absolute inset-0 flex justify-center">
        <div
          className="shrink-0"
          style={{
            width: STAGE_W,
            height: STAGE_H,
            transform: `scale(${scale})`,
            transformOrigin: "top center",
          }}
        >
        <div
          ref={ref}
          onMouseMove={onMove}
          onMouseLeave={onLeave}
          data-testid="hero-3d-scene"
          className="relative w-full h-full select-none"
          style={{ perspective: "1500px" }}
        >
          {/* Ambient afterglow behind the scene */}
          <div className="glow-orb animate-pulse-glow w-[440px] h-[440px] bg-blue-600/30 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
          <div className="glow-orb w-72 h-72 bg-cyan-500/20 right-6 top-6" />
          <div className="glow-orb w-72 h-72 bg-indigo-600/20 left-0 bottom-0" />

          <motion.div
            className="relative w-full h-full"
            style={{ rotateX: rotX, rotateY: rotY, transformStyle: "preserve-3d" }}
          >
            {/* Connection lines */}
            <svg
              className="absolute inset-0 w-full h-full pointer-events-none"
              style={{ transform: "translateZ(10px)" }}
              viewBox="0 0 600 560"
              preserveAspectRatio="none"
              fill="none"
            >
              <defs>
                <linearGradient id="lineGrad" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#0a84ff" stopOpacity="0.1" />
                  <stop offset="50%" stopColor="#3b9bff" stopOpacity="0.7" />
                  <stop offset="100%" stopColor="#0a84ff" stopOpacity="0.1" />
                </linearGradient>
              </defs>
              {[
                "M120,150 C220,120 300,220 360,260",
                "M460,140 C420,220 380,250 360,260",
                "M150,420 C250,400 320,320 360,280",
                "M470,400 C420,360 390,320 370,290",
              ].map((d, i) => (
                <path
                  key={i}
                  d={d}
                  stroke="url(#lineGrad)"
                  strokeWidth="1.5"
                  strokeDasharray="5 7"
                  style={{ animation: `dashFlow ${2.4 + i * 0.4}s linear infinite` }}
                />
              ))}
            </svg>

            {/* ---------- Central: Website / browser mockup ---------- */}
            <FloatLayer
              className="left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px]"
              depth={70}
              delay={0.1}
              duration={7}
              amplitude={12}
            >
              <div className="glass-strong rounded-2xl overflow-hidden shadow-2xl">
                <div className="flex items-center gap-1.5 px-4 py-3 border-b border-white/10 bg-white/[0.03]">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
                  <span className="w-2.5 h-2.5 rounded-full bg-yellow-400/70" />
                  <span className="w-2.5 h-2.5 rounded-full bg-green-400/70" />
                  <div className="ml-3 h-4 flex-1 rounded-full bg-white/5 border border-white/10" />
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div className="h-3 w-20 rounded-full bg-white/15" />
                    <div className="flex gap-2">
                      <div className="h-2.5 w-8 rounded-full bg-white/10" />
                      <div className="h-2.5 w-8 rounded-full bg-white/10" />
                      <div className="h-2.5 w-8 rounded-full bg-white/10" />
                    </div>
                  </div>
                  <div className="h-20 rounded-xl bg-gradient-to-br from-blue-500/30 to-blue-700/10 border border-blue-400/20 mb-4 flex items-center px-4">
                    <div className="space-y-2">
                      <div className="h-2.5 w-28 rounded-full bg-white/40" />
                      <div className="h-2 w-20 rounded-full bg-white/20" />
                      <div className="h-5 w-16 rounded-md bg-blue-500 shadow-[0_0_18px_rgba(10,132,255,0.6)]" />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2.5">
                    {[0, 1, 2].map((i) => (
                      <div key={i} className="h-12 rounded-lg bg-white/5 border border-white/10" />
                    ))}
                  </div>
                </div>
              </div>
            </FloatLayer>

            {/* ---------- CRM dashboard card (top-left) ---------- */}
            <FloatLayer
              className="-left-2 top-6 w-[210px]"
              depth={120}
              delay={0.3}
              duration={6}
              amplitude={14}
            >
              <div className="glass rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-lg bg-blue-500/20 border border-blue-400/30 grid place-items-center text-blue-300">
                    <Database className="w-3.5 h-3.5" strokeWidth={1.8} />
                  </div>
                  <div className="text-[11px] font-semibold text-white/80">CRM Pipeline</div>
                </div>
                <div className="flex items-end gap-1.5 h-16">
                  {[40, 65, 50, 80, 60, 95, 72].map((h, i) => (
                    <motion.div
                      key={i}
                      className="flex-1 rounded-t bg-gradient-to-t from-blue-600/40 to-blue-400"
                      initial={{ height: 0 }}
                      animate={{ height: `${h}%` }}
                      transition={{ duration: 0.8, delay: 0.5 + i * 0.07, ease: "easeOut" }}
                    />
                  ))}
                </div>
                <div className="mt-3 flex items-center justify-between text-[10px] text-white/50">
                  <span>Leads</span>
                  <span className="text-green-400">+24%</span>
                </div>
              </div>
            </FloatLayer>

            {/* ---------- Mobile app screen (right) ---------- */}
            <FloatLayer
              className="-right-2 top-10 w-[130px]"
              depth={150}
              delay={0.45}
              duration={6.5}
              amplitude={16}
            >
              <div className="glass-strong rounded-[1.6rem] p-2 border-white/15">
                <div className="rounded-[1.2rem] overflow-hidden bg-[#0a0a14]">
                  <div className="flex justify-center py-1.5">
                    <div className="w-12 h-1 rounded-full bg-white/20" />
                  </div>
                  <div className="px-3 pb-3 space-y-2.5">
                    <div className="h-14 rounded-xl bg-gradient-to-br from-blue-500/40 to-indigo-600/20 border border-blue-400/20" />
                    <div className="grid grid-cols-2 gap-2">
                      <div className="h-10 rounded-lg bg-white/5 border border-white/10" />
                      <div className="h-10 rounded-lg bg-white/5 border border-white/10" />
                    </div>
                    <div className="h-6 rounded-lg bg-blue-500 shadow-[0_0_16px_rgba(10,132,255,0.6)]" />
                  </div>
                </div>
              </div>
            </FloatLayer>

            {/* ---------- Code panel (bottom-left) ---------- */}
            <FloatLayer
              className="left-4 bottom-4 w-[240px]"
              depth={110}
              delay={0.55}
              duration={7.5}
              amplitude={12}
            >
              <div className="glass rounded-2xl overflow-hidden font-mono text-[10px] leading-relaxed">
                <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10 bg-white/[0.03]">
                  <Code2 className="w-3.5 h-3.5 text-blue-300" strokeWidth={1.8} />
                  <span className="text-white/60">api.integration.ts</span>
                </div>
                <div className="p-3 space-y-1">
                  <div><span className="text-pink-400">const</span> <span className="text-blue-300">api</span> <span className="text-white/50">=</span> <span className="text-emerald-300">connect</span><span className="text-white/50">()</span></div>
                  <div className="text-white/40">await api.<span className="text-yellow-300">sync</span>()</div>
                  <div><span className="text-pink-400">return</span> <span className="text-emerald-300">deploy</span><span className="text-white/50">()</span></div>
                  <div className="flex items-center gap-1.5 text-green-400 pt-1">
                    <CheckCircle2 className="w-3 h-3" /> build passed
                  </div>
                </div>
              </div>
            </FloatLayer>

            {/* ---------- AI automation flow chip (bottom-right) ---------- */}
            <FloatLayer
              className="right-6 bottom-6 w-[195px]"
              depth={130}
              delay={0.65}
              duration={6.8}
              amplitude={15}
            >
              <div className="glass rounded-2xl p-3.5">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-lg bg-indigo-500/20 border border-indigo-400/30 grid place-items-center text-indigo-300">
                    <Workflow className="w-3.5 h-3.5" strokeWidth={1.8} />
                  </div>
                  <div className="text-[11px] font-semibold text-white/80">AI Automation</div>
                </div>
                <div className="flex items-center justify-between gap-1.5">
                  {[
                    { icon: Zap, label: "Trigger" },
                    { icon: Cpu, label: "AI" },
                    { icon: CheckCircle2, label: "Action" },
                  ].map((n, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <div className="flex flex-col items-center gap-1">
                        <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 grid place-items-center text-blue-300">
                          <n.icon className="w-3.5 h-3.5" strokeWidth={1.8} />
                        </div>
                        <span className="text-[8px] text-white/40">{n.label}</span>
                      </div>
                      {i < 2 && <div className="w-3 h-px bg-blue-400/50" />}
                    </div>
                  ))}
                </div>
              </div>
            </FloatLayer>

            {/* ---------- API node pills (floating) ---------- */}
            <FloatLayer
              className="left-1/2 -translate-x-1/2 top-0 flex gap-2"
              depth={170}
              delay={0.8}
              duration={5.5}
              amplitude={10}
            >
              {["Stripe", "OpenAI", "Webhook"].map((label) => (
                <div
                  key={label}
                  className="eq-chip !text-[10px] !tracking-normal !py-1.5 !px-2.5 text-blue-200 normal-case"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-400 shadow-[0_0_8px_rgba(10,132,255,0.9)]" />
                  {label}
                </div>
              ))}
            </FloatLayer>
          </motion.div>
        </div>
        </div>
      </div>
    </div>
  );
}
